import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, TrendingUp, Award, Users, Leaf } from 'lucide-react';
import { HOME_PAGE } from '../content';

const Home = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-24 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-green-50 via-white to-pink-50 -z-10" />
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-block px-4 py-2 bg-gradient-to-r from-green-100 to-emerald-100 rounded-full">
                <span className="text-green-700 font-semibold text-sm">{HOME_PAGE.hero.badge}</span>
              </div>
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                {HOME_PAGE.hero.title} <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-emerald-600">{HOME_PAGE.hero.titleHighlight}</span> {HOME_PAGE.hero.titleEnd}
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed">
                {HOME_PAGE.hero.description}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/partnership">
                  <Button size="lg" className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white group">
                    {HOME_PAGE.hero.cta1}
                    <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
                  </Button>
                </Link>
                <Link to="/careers">
                  <Button size="lg" variant="outline" className="border-green-600 text-green-600 hover:bg-green-50">
                    {HOME_PAGE.hero.cta2}
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl transform hover:scale-105 transition-transform duration-500">
                <img
                  src="https://images.unsplash.com/photo-1736897056101-5d1e798d112d?w=800&q=80"
                  alt="Modern office building"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-lg">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-pink-600 rounded-lg flex items-center justify-center">
                    <TrendingUp className="text-white" size={24} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">+60%</p>
                    <p className="text-sm text-gray-600">Croissance 2024-2025</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="pt-16 pb-0 bg-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            {HOME_PAGE.stats.map((stat, index) => (
              <div key={index} className="text-center">
                <p className="text-4xl lg:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-400 mb-2">
                  {stat.value}
                </p>
                <p className="text-gray-300">{stat.label}</p>
              </div>
            ))}
          </div>
          
          {/* Partner Logo */}
          <div className="border-t border-gray-800 pt-8 border-b pb-2.5">
            <p className="text-center text-gray-400 mb-4 text-sm uppercase tracking-wider">Notre partenaire</p>
            <div className="flex flex-col items-center gap-2 mb-2.5">
              <img 
                src="https://customer-assets.emergentagent.com/job_rt-energy/artifacts/5zlvdxal_octopus%20energy%20-%20Copie.png" 
                alt="Octopus Energy France" 
                className="h-32 w-auto hover:scale-110 transition-transform duration-300"
              />
              <p className="text-2xl font-bold text-white">Octopus Energie</p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="pt-8 pb-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{HOME_PAGE.values.sectionTitle}</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {HOME_PAGE.values.sectionDescription}
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {HOME_PAGE.values.items.map((value, index) => {
              const icons = [Award, TrendingUp, Users, Leaf];
              const IconComponent = icons[index] || Award;
              return (
                <div
                  key={index}
                  className="group p-8 rounded-2xl bg-white border border-gray-200 hover:border-green-500 hover:shadow-xl transition-all duration-300"
                >
                  <div className="w-14 h-14 bg-gradient-to-br from-green-100 to-emerald-100 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                    <IconComponent className="text-green-600" size={28} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-green-600 via-emerald-600 to-green-700 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-pink-300 rounded-full blur-3xl" />
        </div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
              {HOME_PAGE.cta.title}
            </h2>
            <p className="text-xl text-green-50 mb-8">
              {HOME_PAGE.cta.description}
            </p>
            <Link to="/careers">
              <Button size="lg" className="bg-white text-green-600 hover:bg-gray-100 shadow-lg group">
                {HOME_PAGE.cta.buttonText}
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;